import {
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";
import { NotificationContext } from "../../../contexts/data/NotificationContext";
import useServiceStackClient from "../../../hooks/api/useServiceStackClient";
import { GetUnreadNotificationCount } from "../../../api/dtos/dtos";
import AuthContext from "../../../contexts/auth/AuthContext";

/**
 * --- Interfaces ---
 */
export interface INotificationProviderProps extends PropsWithChildren {}

/**
 * --- Notification Provider ---
 */
export const NotificationProvider: FC<INotificationProviderProps> = ({
    children
}) => {

    const client = useServiceStackClient();

    const { user } = useContext(AuthContext);

    const [totalCount, setTotalCount] = useState<number | undefined>(undefined);

    const refresh = useCallback(async () => {
        if (!user) {
            setTotalCount(undefined);
            return;
        }

        const request = new GetUnreadNotificationCount({
            userId: user.id,
        });

        const result = await client.get(request);

        setTotalCount(result.totalCount);

        return result;
    }, [client, user]);

    useEffect(() => {
        if (!user) {
            setTotalCount(undefined);
            return;
        }

        // Initial load
        void refresh();

        // Refresh every 3 minute
        const intervalId = setInterval(() => {
            void refresh();
        }, 180_000);

        return () => clearInterval(intervalId);
    }, [user, refresh]);

    const context = useMemo(() => ({
        totalCount,
        setTotalCount,
        refresh
    }), [totalCount, refresh]);

    return (
        <NotificationContext.Provider value={context}>
            {children}
        </NotificationContext.Provider>
    );
};